import React from 'react';
import classNames from 'classnames';

import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney } from '../../util/currency';
import { createSlug } from '../../util/urlHelpers';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import NamedLink from '../NamedLink/NamedLink';

import css from './OrderItemList.module.css';

const { Money } = sdkTypes;

/**
 * A link to the listing, or plain content when there is nothing to link to. Mirrors
 * CartItemRow's own ListingLinkMaybe: once this component is reused for an existing order
 * (Task 15), a listing can be deleted, and sending the buyer to a dead end from their own
 * order history adds nothing.
 */
const ListingLinkMaybe = ({ listingId, title, children }) =>
  listingId && title ? (
    <NamedLink name="ListingPage" params={{ id: listingId, slug: createSlug(title) }}>
      {children}
    </NamedLink>
  ) : (
    <>{children}</>
  );

/**
 * CL-CART-01 — the item list shown on the checkout page, and later reused for the order
 * confirmation, transaction and inbox screens (Task 15).
 *
 * Read-only, on purpose: this is a record of what is being bought, or what was bought.
 * Editing a basket happens only on the cart page. Each of the four call sites feeds this
 * the same shape regardless of where the items came from — the annotated cart on checkout,
 * `protectedData.cart.items` once a transaction exists — so this component never has to
 * know which page it is on.
 */
const OrderItemList = props => {
  const { className, rootClassName, items = [] } = props;
  const intl = useIntl();

  const price = (amount, currency) =>
    Number.isInteger(amount) && currency ? formatMoney(intl, new Money(amount, currency)) : null;

  return (
    <ul className={classNames(rootClassName || css.root, className)}>
      {items.map(item => {
        const unitPrice = price(item.unitPriceInSubunits, item.currency);
        const lineTotal = price(item.lineTotalInSubunits, item.currency);

        // A row can legitimately have no price: a listing type with `displayPrice: false` on the
        // inquiry checkout, or a cart snapshot that recorded none. Without this the meta line
        // rendered a bare " each", and the total an empty cell.
        const meta = unitPrice ? (
          item.quantity > 1 ? (
            <FormattedMessage
              id="OrderItemList.quantityAndUnitPrice"
              values={{ quantity: item.quantity, price: unitPrice }}
            />
          ) : (
            <FormattedMessage id="OrderItemList.unitPrice" values={{ price: unitPrice }} />
          )
        ) : item.quantity > 1 ? (
          <FormattedMessage id="OrderItemList.quantity" values={{ quantity: item.quantity }} />
        ) : null;

        return (
          <li key={item.listingId} className={css.item}>
            <ListingLinkMaybe listingId={item.listingId} title={item.title}>
              {item.imageUrl ? (
                <img className={css.thumb} src={item.imageUrl} alt={item.title || ''} />
              ) : (
                <div className={css.thumbPlaceholder} />
              )}
            </ListingLinkMaybe>
            <div className={css.details}>
              <p className={css.title}>
                <ListingLinkMaybe listingId={item.listingId} title={item.title}>
                  {item.title}
                </ListingLinkMaybe>
              </p>
              {meta ? <p className={css.meta}>{meta}</p> : null}
            </div>
            {lineTotal ? <div className={css.lineTotal}>{lineTotal}</div> : null}
          </li>
        );
      })}
    </ul>
  );
};

export default OrderItemList;
